import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { supabase, type Profile } from '@product-radar/shared';
import type { User } from '@supabase/supabase-js';

export interface Workspace {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string;
  settings: any;
  subscription_tier: 'free' | 'starter' | 'professional' | 'enterprise';
  subscription_status: 'active' | 'trial' | 'cancelled' | 'suspended';
  created_at: string;
  company_id: string | null;
}

export interface Company {
  id: string;
  name: string;
  industry: string | null;
  company_size: string | null;
  timezone: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_subscription_status: string | null;
  plan: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RegionalSettings {
  language: string;
  currency: string;
  dateFormat: 'mdy' | 'dmy' | 'ymd' | 'iso';
  timeFormat: '12h' | '24h';
  timezone: string;
}

export interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  role: 'admin' | 'product_manager' | 'contributor' | 'viewer';
  status: 'active' | 'invited' | 'suspended';
  joined_at: string;
  last_active_at?: string | null;
}

interface WorkspaceContextType {
  workspace: Workspace | null;
  company: Company | null;
  membership: WorkspaceMember | null;
  userProfile: Profile | null;
  isLoading: boolean;
  error: string | null;
  refreshWorkspace: () => Promise<void>;
  refreshCompany: () => Promise<void>;
  userRole: 'admin' | 'product_manager' | 'contributor' | 'viewer' | null;
  setCurrentWorkspace: (workspaceId: string | null) => Promise<void>;
  regionalSettings: RegionalSettings;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ 
  children, 
  workspaceId: initialWorkspaceId
}: { 
  children: ReactNode; 
  workspaceId?: string;
}) {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [membership, setMembership] = useState<WorkspaceMember | null>(null);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | undefined>(initialWorkspaceId);
  const loadInFlightRef = useRef<{ workspaceId: string; promise: Promise<void> } | null>(null);

  const resetState = useCallback(() => {
    setWorkspace(null);
    setCompany(null);
    setMembership(null);
    setUserProfile(null);
    setError(null);
  }, []);

  const loadWorkspace = useCallback(async (workspaceId?: string | null) => {
    console.log('[WorkspaceProvider] loadWorkspace called', { workspaceId });
    
    if (!workspaceId) {
      loadInFlightRef.current = null;
      console.log('[WorkspaceProvider] no workspaceId, resetting state');
      resetState();
      setCurrentWorkspaceId(undefined);
      setIsLoading(false);
      return;
    }

    // Prevent concurrent loads of the same workspace
    const inFlight = loadInFlightRef.current;
    if (inFlight?.workspaceId === workspaceId) {
      await inFlight.promise;
      return;
    }

    const doLoad = async () => {
      try {
        console.log('[WorkspaceProvider] starting workspace load', workspaceId);
        setIsLoading(true);
        setError(null);
        setCurrentWorkspaceId(workspaceId);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error('[WorkspaceProvider] no user found');
          setError('Not authenticated');
          setIsLoading(false);
          return;
        }

        console.log('[WorkspaceProvider] fetching membership');
        const { data: membershipData, error: membershipError } = await supabase
          .rpc('get_membership_for_user', {
            p_workspace_id: workspaceId,
          });

        if (membershipError) {
          console.error('[WorkspaceProvider] Error loading membership:', membershipError);
          setError('Failed to load workspace membership');
          setIsLoading(false);
          return;
        }

        if (!membershipData) {
          console.warn('[WorkspaceProvider] no membership found');
          try {
            await supabase.rpc('clear_last_active_workspace', {
              p_user_id: user.id,
            });
          } catch (clearError) {
            console.warn('Failed to clear last active workspace:', clearError);
          }

          resetState();
          setCurrentWorkspaceId(undefined);
          setIsLoading(false);
          return;
        }

        console.log('[WorkspaceProvider] fetching workspace data');
        const { data: workspaceData, error: workspaceError } = await supabase
          .from('workspaces')
          .select('*')
          .eq('id', workspaceId)
          .single();

        if (workspaceError || !workspaceData) {
          console.error('[WorkspaceProvider] Error loading workspace:', workspaceError);
          setError('Workspace not found');
          setIsLoading(false);
          return;
        }

        // Fetch company if workspace has a company_id
        let companyData: Company | null = null;
        if (workspaceData.company_id) {
          console.log('[WorkspaceProvider] fetching company', workspaceData.company_id);
          const { data: compData, error: compError } = await supabase
            .from('companies')
            .select('*')
            .eq('id', workspaceData.company_id)
            .single();

          if (compError) {
            console.warn('[WorkspaceProvider] Error loading company:', compError);
          } else {
            companyData = compData as Company;
          }
        }

        console.log('[WorkspaceProvider] fetching profile');
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        console.log('[WorkspaceProvider] workspace loaded successfully');
        setWorkspace(workspaceData);
        setCompany(companyData);
        setMembership(membershipData);
        setUserProfile(profileData as Profile | null);
        setIsLoading(false);

        try {
          await supabase.rpc('update_last_active_workspace', {
            p_workspace_id: workspaceId,
            p_user_id: user.id
          });
        } catch (updateError) {
          console.error('Error updating last active workspace:', updateError);
        }
      } catch (err: any) {
        console.error('[WorkspaceProvider] Error loading workspace:', err);
        setError(err.message || 'Failed to load workspace');
        setIsLoading(false);
      } finally {
        if (loadInFlightRef.current?.workspaceId === workspaceId) {
          loadInFlightRef.current = null;
        }
      }
    };

    const promise = doLoad();
    loadInFlightRef.current = { workspaceId, promise };
    await promise;
  }, [resetState]);

  const setCurrentWorkspace = async (workspaceId: string | null) => {
    await loadWorkspace(workspaceId);
  };

  useEffect(() => {
    console.log('[WorkspaceProvider] useEffect triggered', { initialWorkspaceId, currentWorkspaceId });

    if (initialWorkspaceId) {
      // Always load the initial workspace on mount / when prop changes.
      // We intentionally ignore currentWorkspaceId here to avoid skipping the first load.
      console.log('[WorkspaceProvider] loading initial workspace from prop');
      loadWorkspace(initialWorkspaceId);
    } else {
      // No workspaceId: mark as not loading so the UI can render the selector.
      console.log('[WorkspaceProvider] no initialWorkspaceId, setting isLoading = false');
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialWorkspaceId]);

  const refreshWorkspace = useCallback(async () => {
    if (currentWorkspaceId) {
      await loadWorkspace(currentWorkspaceId);
    } else {
      await loadWorkspace(null);
    }
  }, [currentWorkspaceId, loadWorkspace]);

  const refreshCompany = async () => {
    if (!workspace?.company_id) return;
    try {
      const { data: compData, error: compError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', workspace.company_id)
        .single();
      if (!compError && compData) {
        setCompany(compData as Company);
      }
    } catch (err) {
      console.error('[WorkspaceProvider] Error refreshing company:', err);
    }
  };

  const computeRegionalSettings = (): RegionalSettings => {
    const settings = (workspace as any)?.settings || {};
    const browserLang =
      typeof navigator !== 'undefined'
        ? (navigator.language || 'en').split('-')[0]
        : 'en';

    const language = settings.language || browserLang || 'en';
    const currency = settings.currency || 'usd';

    const browserLocale =
      typeof navigator !== 'undefined' ? navigator.language || 'en-US' : 'en-US';
    const defaultDateFormat =
      settings.date_format ||
      (browserLocale === 'en-US' ? 'mdy' : 'dmy');

    const defaultTimeFormat =
      settings.time_format ||
      (browserLocale === 'en-US' ? '12h' : '24h');

    const defaultTimezone =
      settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    return {
      language,
      currency,
      dateFormat: defaultDateFormat,
      timeFormat: defaultTimeFormat,
      timezone: defaultTimezone,
    };
  };

  const regionalSettings = computeRegionalSettings();

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        company,
        membership,
        userProfile,
        isLoading,
        error,
        refreshWorkspace,
        refreshCompany,
        userRole: membership?.role ?? null,
        setCurrentWorkspace,
        regionalSettings,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

export function useRegionalSettings() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useRegionalSettings must be used within a WorkspaceProvider');
  }
  return context.regionalSettings;
}

// Helper function to get user's last active workspace
export async function getLastActiveWorkspace(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No user found when getting last active workspace');
      return null;
    }

    console.log('Getting last active workspace for user:', user.id);
    const { data, error } = await supabase.rpc('get_last_active_workspace', {
      p_user_id: user.id
    });

    if (error) {
      console.error('Error getting last active workspace:', error);
      return null;
    }

    console.log('Last active workspace ID:', data);
    return data;
  } catch (error) {
    console.error('Error getting last active workspace:', error);
    return null;
  }
}

// Helper function to update user's last active workspace
export async function updateLastActiveWorkspace(workspaceId: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No user found when updating last active workspace');
      return;
    }

    const { error } = await supabase.rpc('update_last_active_workspace', {
      p_workspace_id: workspaceId,
      p_user_id: user.id
    });

    if (error) {
      console.error('Error updating last active workspace:', error);
    }
  } catch (error) {
    console.error('Error updating last active workspace:', error);
  }
}

// =====================================================
// Invitation context + helpers
// =====================================================

let invitationEmailColumnAvailable = true;

const invitationSelectFields = (includeEmail = true) => `
      id,
      workspace_id,
      role,
      status,
      expires_at,
      ${includeEmail ? 'invitation_email,' : ''}
      workspaces!inner (
        id,
        name
      )
    `;

export interface PendingInvitation {
  membership: {
    id: string;
    workspace_id: string;
    role: WorkspaceMember['role'];
    status: WorkspaceMember['status'];
    expires_at?: string | null;
  };
  workspaceName: string;
  invitationEmail?: string | null;
}

export async function fetchPendingInvitationsByUserId(userId: string): Promise<PendingInvitation[]> {
  const includeEmail = invitationEmailColumnAvailable;
  const { data, error } = await supabase
    .from('workspace_members')
    .select(invitationSelectFields(includeEmail))
    .eq('user_id', userId)
    .eq('status', 'invited');

  if (error) {
    if (error.code === '42703' && includeEmail) {
      console.warn('invitation_email column missing; disabling email-based invitation lookups.');
      invitationEmailColumnAvailable = false;
      return fetchPendingInvitationsByUserId(userId);
    }
    console.error('Failed to fetch pending invitations by user ID:', error);
    return [];
  }

  // Filter out expired invitations
  const now = new Date();
  const validInvitations = (data || []).filter((item: any) => {
    if (!item.expires_at) {
      return true; // No expiration set
    }
    return new Date(item.expires_at) >= now;
  });

  return validInvitations.map((item: any) => ({
    membership: {
      id: item.id,
      workspace_id: item.workspace_id,
      role: item.role,
      status: item.status,
      expires_at: item.expires_at,
    },
    workspaceName: item.workspaces?.name || 'Unknown Workspace',
    invitationEmail: includeEmail ? item.invitation_email : undefined,
  }));
}

export async function fetchPendingInvitationsByEmail(email: string): Promise<PendingInvitation[]> {
  const normalizedEmail = email?.toLowerCase();
  if (!normalizedEmail) return [];

  if (!invitationEmailColumnAvailable) {
    console.warn('Skipping invitation email lookup because invitation_email column is unavailable.');
    return [];
  }

  const { data, error } = await supabase
    .from('workspace_members')
    .select(invitationSelectFields(true))
    .eq('invitation_email', normalizedEmail)
    .eq('status', 'invited')
    .is('user_id', null);

  if (error) {
    if (error.code === '42703') {
      console.warn('invitation_email column missing; disabling email-based invitation lookups.');
      invitationEmailColumnAvailable = false;
      return [];
    }
    console.error('Failed to fetch pending invitations by email:', error);
    return [];
  }

  // Filter out expired invitations
  const now = new Date();
  const validInvitations = (data || []).filter((item: any) => {
    if (!item.expires_at) {
      return true; // No expiration set
    }
    return new Date(item.expires_at) >= now;
  });

  return validInvitations.map((item: any) => ({
    membership: {
      id: item.id,
      workspace_id: item.workspace_id,
      role: item.role,
      status: item.status,
      expires_at: item.expires_at,
    },
    workspaceName: item.workspaces?.name || 'Unknown Workspace',
    invitationEmail: item.invitation_email,
  }));
}

interface InvitationContextValue {
  pendingInvitations: PendingInvitation[];
  isLoading: boolean;
  refreshInvitations: () => Promise<PendingInvitation[]>;
  clearInvitations: () => void;
}

const InvitationContext = createContext<InvitationContextValue | undefined>(undefined);

export function InvitationsProvider({ children }: { children: ReactNode }) {
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refreshInvitations = useCallback(
    async (userOverride?: User | null): Promise<PendingInvitation[]> => {
      console.log('[refreshInvitations] start', { hasUserOverride: !!userOverride });
      setIsLoading(true);
      try {
        let user = userOverride;
        if (typeof userOverride === 'undefined') {
          const {
            data: { user: fetchedUser },
            error: fetchError,
          } = await supabase.auth.getUser();
          if (fetchError) {
            console.error('[refreshInvitations] getUser error', fetchError);
          }
          user = fetchedUser;
        }

        console.log('[refreshInvitations] fetched user', user?.id);
        if (!user) {
          console.log('[refreshInvitations] no user');
          setPendingInvitations([]);
          return [];
        }

        // Use RPC function to bypass RLS issues
        console.log('[refreshInvitations] calling get_pending_invitations RPC');
        const { data, error } = await supabase.rpc('get_pending_invitations');

        if (error) {
          console.error('[refreshInvitations] RPC error', error);
          setPendingInvitations([]);
          return [];
        }

        console.log('[refreshInvitations] RPC result', data);

        // Transform RPC result to PendingInvitation format
        const invitations: PendingInvitation[] = (data || []).map((item: any) => ({
          membership: {
            id: item.membership_id,
            workspace_id: item.membership_workspace_id,
            role: item.membership_role,
            status: item.membership_status,
            expires_at: item.membership_expires_at,
          },
          workspaceName: item.workspace_name || 'Unknown Workspace',
          invitationEmail: item.invitation_email,
        }));

        setPendingInvitations(invitations);
        return invitations;
      } catch (error) {
        console.error('Failed to refresh pending invitations:', error);
        setPendingInvitations([]);
        return [];
      } finally {
        console.log('[refreshInvitations] done');
        setIsLoading(false);
      }
    },
    []
  );

  const clearInvitations = useCallback(() => {
    setPendingInvitations([]);
  }, []);

  useEffect(() => {
    refreshInvitations();
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        clearInvitations();
        setIsLoading(false);
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        refreshInvitations(session?.user ?? undefined);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, [refreshInvitations, clearInvitations]);

  return (
    <InvitationContext.Provider
      value={{
        pendingInvitations,
        isLoading,
        refreshInvitations,
        clearInvitations,
      }}
    >
      {children}
    </InvitationContext.Provider>
  );
}

export function useInvitations() {
  const context = useContext(InvitationContext);
  if (!context) {
    throw new Error('useInvitations must be used within an InvitationsProvider');
  }
  return context;
}
